import { Menu as MenuPrimitive } from '@base-ui/react';
import { twMerge } from 'tailwind-merge';
import type { ComponentProps } from 'react';

export const MenuRoot = MenuPrimitive.Root;
export const MenuTrigger = MenuPrimitive.Trigger;

export const MenuPortal = MenuPrimitive.Portal;

export interface MenuPositionerProps extends ComponentProps<
  typeof MenuPrimitive.Positioner
> {}

export function MenuPositioner({ className, ...props }: MenuPositionerProps) {
  return (
    <MenuPrimitive.Positioner
      data-slot="menu-positioner"
      className={twMerge(
        'z-50',
        typeof className === 'string' ? className : undefined
      )}
      {...props}
    />
  );
}

export interface MenuPopupProps extends ComponentProps<
  typeof MenuPrimitive.Popup
> {}

export function MenuPopup({ className, ...props }: MenuPopupProps) {
  return (
    <MenuPrimitive.Popup
      data-slot="menu-popup"
      className={twMerge(
        'min-w-45 max-h-75 overflow-y-auto rounded-lg border border-border bg-surface p-1 shadow-lg',
        'origin-(--transform-origin)',
        'data-starting-style:animate-in data-[starting-style]:fade-in-0 data-[starting-style]:zoom-in-95',
        'data-ending-style:animate-out data-[ending-style]:fade-out-0 data-[ending-style]:zoom-out-95',
        typeof className === 'string' ? className : undefined
      )}
      {...props}
    />
  );
}

export interface MenuItemProps extends ComponentProps<
  typeof MenuPrimitive.Item
> {}

export function MenuItem({ className, ...props }: MenuItemProps) {
  return (
    <MenuPrimitive.Item
      data-slot="menu-item"
      className={twMerge(
        'flex cursor-pointer select-none items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground outline-none',
        'transition-colors hover:bg-muted focus:bg-muted',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        typeof className === 'string' ? className : undefined
      )}
      {...props}
    />
  );
}

export interface MenuSeparatorProps extends ComponentProps<
  typeof MenuPrimitive.Separator
> {}

export function MenuSeparator({ className, ...props }: MenuSeparatorProps) {
  return (
    <MenuPrimitive.Separator
      data-slot="menu-separator"
      className={twMerge(
        'my-1 h-px bg-border',
        typeof className === 'string' ? className : undefined
      )}
      {...props}
    />
  );
}

export interface MenuGroupProps extends ComponentProps<
  typeof MenuPrimitive.Group
> {}

export function MenuGroup({ className, ...props }: MenuGroupProps) {
  return (
    <MenuPrimitive.Group
      data-slot="menu-group"
      className={twMerge(
        '',
        typeof className === 'string' ? className : undefined
      )}
      {...props}
    />
  );
}

export interface MenuGroupLabelProps extends ComponentProps<
  typeof MenuPrimitive.GroupLabel
> {}

export function MenuGroupLabel({ className, ...props }: MenuGroupLabelProps) {
  return (
    <MenuPrimitive.GroupLabel
      data-slot="menu-group-label"
      className={twMerge(
        'px-3 py-1.5 text-xs font-medium text-muted-foreground',
        typeof className === 'string' ? className : undefined
      )}
      {...props}
    />
  );
}
